import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Upload, Palette, ArrowRight } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TF_LOGO = "https://www.targetfunder.com/tf_logo.png";

// Multi-sport demo support — `?sport=` query param picks the predictor flow.
// Marketing site URLs (post WC kickoff — Golf is now the default):
//   /demo/create              → Open Golf 2026 demo (default — active product)
//   /demo/create?sport=golf   → Open Golf 2026 demo (explicit)
//   /demo/create?sport=wc     → World Cup 2026 demo (legacy — entries closed,
//                                but the demo flow still renders so anyone
//                                reviewing 2026 history can see it)
const SPORTS = {
  wc_2026: {
    predictor_type: 'wc_2026',
    eyebrow: 'Free Preview',
    title: 'See Your Fundraiser',
    intro: "Enter your club details below and we'll show you exactly what your branded World Cup 2026 Predictor would look like. Takes 30 seconds.",
    cta: 'See My Demo',
  },
  golf_open_2026: {
    predictor_type: 'golf_open_2026',
    eyebrow: 'Free Preview · Open Golf 2026',
    title: 'See Your Golf Predictor',
    intro: "Enter your club details and we'll show you exactly what your branded Open Golf 2026 Predictor would look like — full Royal Birkdale field, 4-pick odds-balanced format. Takes 30 seconds.",
    cta: 'See My Golf Demo',
  },
  gaa_final_2026: {
    predictor_type: 'gaa_final_2026',
    eyebrow: 'Free Preview · All-Ireland Final 2026',
    title: 'See Your Final Score Predictor',
    intro: "Enter your club details and we'll show you exactly what your branded All-Ireland Final Score Predictor would look like — Kerry v Mayo, Croke Park, Sunday 26 July. Takes 30 seconds.",
    cta: 'See My Demo',
  },
};

export default function DemoCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Default flipped to Golf — World Cup is in leaderboard mode (entries closed)
  // and Open Golf is the active product. Explicit ?sport=wc still works for
  // legacy links and history review. ?sport=gaa → All-Ireland Final flow.
  const sportParam = searchParams.get('sport');
  const sportKey = sportParam === 'wc' ? 'wc_2026' : sportParam === 'gaa' ? 'gaa_final_2026' : 'golf_open_2026';
  const sport = SPORTS[sportKey];

  // Set the browser tab title to match the sport flow.
  useEffect(() => {
    const previous = document.title;
    document.title = sportKey === 'golf_open_2026'
      ? 'Open Golf 2026 Predictor | Demo'
      : sportKey === 'gaa_final_2026'
        ? 'All-Ireland Final 2026 Predictor | Demo'
        : 'World Cup 2026 Predictor | Demo';
    return () => { document.title = previous; };
  }, [sportKey]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [logoPreview, setLogoPreview] = useState(null);
  const [form, setForm] = useState({
    club_name: '',
    primary_colour: '#1a5632',
    secondary_colour: '#ffffff',
    currency: 'EUR',
    entry_fee: 10,
    logo: '',
    logo_filename: '',
    first_name: '',
    email: '',
    role: 'Chairperson',
    marketing_opt_in: false,
  });

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Logo must be under 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      setForm(f => ({ ...f, logo: base64, logo_filename: file.name }));
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.club_name.trim()) {
      setError('Please enter your club name');
      return;
    }
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      setError('Please enter a valid email address');
      return;
    }
    if (!form.first_name.trim()) {
      setError('Please enter your first name');
      return;
    }
    if (!form.role) {
      setError('Please pick your role at the club');
      return;
    }
    setError('');
    setCreating(true);
    try {
      const res = await axios.post(`${API}/demo/create`, { ...form, predictor_type: sport.predictor_type });
      // Backend returns demo_url scoped to the predictor — use it so Golf
      // demos go to /demo/golf/{id} (rendering the picker), not /demo/{id}.
      navigate(res.data.demo_url || `/demo/${res.data.demo_id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create demo');
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-[#1D3557]/5 px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <img src={TF_LOGO} alt="TargetFunder" className="h-10 object-contain" />
          <div>
            <h1 className="text-lg font-black uppercase tracking-tight text-[#1D3557]">
              target<span className="text-[#E63946]">funder</span><span className="text-[#1D3557]/30">.com</span>
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-10">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E63946] mb-3">{sport.eyebrow}</p>
        <h2 className="font-black text-4xl sm:text-5xl uppercase tracking-tight leading-none text-[#1D3557] mb-4" data-testid="demo-create-title">
          {sport.title}
        </h2>
        <p className="text-base text-[#1D3557]/55 leading-relaxed max-w-lg mb-8">
          {sport.intro}
        </p>

        {error && (
          <div className="mb-6 p-4 bg-[#E63946]/5 border border-[#E63946]/20 text-[#E63946] text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} data-testid="demo-create-form">
          <div className="space-y-5">
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#1D3557]/50 mb-1.5">Club Name *</label>
              <input
                type="text"
                value={form.club_name}
                onChange={e => setForm(f => ({ ...f, club_name: e.target.value }))}
                className="w-full bg-white border border-[#1D3557]/10 text-[#1D3557] px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#1D3557] focus:border-[#1D3557] placeholder:text-[#1D3557]/25 transition-colors"
                placeholder="e.g. Ballymore FC"
                required
                data-testid="demo-club-name"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#1D3557]/50 mb-1.5">
                  <Palette size={12} weight="bold" className="inline mr-1" />
                  Primary Colour
                </label>
                <div className="flex">
                  <input
                    type="color"
                    value={form.primary_colour}
                    onChange={e => setForm(f => ({ ...f, primary_colour: e.target.value }))}
                    className="w-12 h-[46px] cursor-pointer border border-[#1D3557]/10 border-r-0"
                    data-testid="demo-primary-colour"
                  />
                  <input
                    type="text"
                    value={form.primary_colour}
                    onChange={e => setForm(f => ({ ...f, primary_colour: e.target.value }))}
                    className="flex-1 bg-white border border-[#1D3557]/10 text-[#1D3557] px-4 py-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1D3557] transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#1D3557]/50 mb-1.5">Secondary Colour</label>
                <div className="flex">
                  <input
                    type="color"
                    value={form.secondary_colour}
                    onChange={e => setForm(f => ({ ...f, secondary_colour: e.target.value }))}
                    className="w-12 h-[46px] cursor-pointer border border-[#1D3557]/10 border-r-0"
                    data-testid="demo-secondary-colour"
                  />
                  <input
                    type="text"
                    value={form.secondary_colour}
                    onChange={e => setForm(f => ({ ...f, secondary_colour: e.target.value }))}
                    className="flex-1 bg-white border border-[#1D3557]/10 text-[#1D3557] px-4 py-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1D3557] transition-colors"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#1D3557]/50 mb-1.5">Club Logo (optional)</label>
              <div
                className="border border-[#1D3557]/10 p-4 text-center cursor-pointer hover:border-[#1D3557]/30 transition-colors"
                onClick={() => document.getElementById('demo-logo-input').click()}
                data-testid="demo-logo-upload"
              >
                <input id="demo-logo-input" type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
                {logoPreview ? (
                  <div className="flex items-center justify-center gap-4">
                    <img src={logoPreview} alt="Logo preview" className="w-14 h-14 object-contain" />
                    <div className="text-left">
                      <p className="text-sm text-[#1D3557] font-medium">{form.logo_filename}</p>
                      <p className="text-xs text-[#1D3557]/40">Click to replace</p>
                    </div>
                  </div>
                ) : (
                  <div className="py-2">
                    <Upload size={20} className="mx-auto text-[#1D3557]/25 mb-1" />
                    <p className="text-sm text-[#1D3557]/40">Click to upload your club crest</p>
                    <p className="text-xs text-[#1D3557]/45">A screenshot from your website is fine here</p>
                  </div>
                )}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#1D3557]/50 mb-1.5">Entry Fee</label>
                <input
                  type="number"
                  step="0.01"
                  min="1"
                  value={form.entry_fee}
                  onChange={e => setForm(f => ({ ...f, entry_fee: parseFloat(e.target.value) || 10 }))}
                  className="w-full bg-white border border-[#1D3557]/10 text-[#1D3557] px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#1D3557] transition-colors"
                  data-testid="demo-entry-fee"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#1D3557]/50 mb-1.5">Currency</label>
                <select
                  value={form.currency}
                  onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                  className="w-full bg-white border border-[#1D3557]/10 text-[#1D3557] px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#1D3557] transition-colors"
                  data-testid="demo-currency"
                >
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </div>

            {/* Your details — required so we can send a reminder before the demo expires */}
            <div className="pt-5 border-t border-[#1D3557]/5 space-y-4">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E63946]">Your Details</p>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#1D3557]/50 mb-1.5">First name *</label>
                  <input
                    type="text"
                    value={form.first_name}
                    onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))}
                    className="w-full bg-white border border-[#1D3557]/10 text-[#1D3557] px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#1D3557] focus:border-[#1D3557] placeholder:text-[#1D3557]/25 transition-colors"
                    placeholder="Mark"
                    required
                    data-testid="demo-first-name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#1D3557]/50 mb-1.5">Email *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full bg-white border border-[#1D3557]/10 text-[#1D3557] px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#1D3557] focus:border-[#1D3557] placeholder:text-[#1D3557]/25 transition-colors"
                    placeholder="you@club.com"
                    required
                    data-testid="demo-email"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#1D3557]/50 mb-1.5">Role at Club *</label>
                  <select
                    value={form.role}
                    onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                    className="w-full bg-white border border-[#1D3557]/10 text-[#1D3557] px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#1D3557] transition-colors"
                    required
                    data-testid="demo-role"
                  >
                    <option value="Chairperson">Chairperson</option>
                    <option value="Treasurer">Treasurer</option>
                    <option value="Secretary">Secretary</option>
                    <option value="Committee Member">Committee Member</option>
                    <option value="PRO">PRO</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <p className="text-xs text-[#1D3557]/50 leading-relaxed">
                {sportKey === 'wc_2026'
                  ? <>We&apos;ll send you one reminder before your demo expires (7 days) so you don&apos;t lose your design.</>
                  : <>We&apos;ll email you the night before your demo expires so you don&apos;t lose your design.</>}
              </p>
              <label className="flex items-start gap-2.5 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={form.marketing_opt_in}
                  onChange={e => setForm(f => ({ ...f, marketing_opt_in: e.target.checked }))}
                  className="mt-0.5 w-4 h-4 rounded border-[#1D3557]/25 text-[#1D3557] focus:ring-[#1D3557]"
                  data-testid="demo-marketing-opt-in"
                />
                <span className="text-xs text-[#1D3557]/70 leading-relaxed group-hover:text-[#1D3557] transition-colors">
                  Also keep me posted about new TargetFunder fundraisers launching in 2026–2027 — e.g. Open Golf, US Masters, All-Ireland Final Score Predictor.
                </span>
              </label>
            </div>
          </div>

          <button
            type="submit"
            disabled={creating}
            className="w-full mt-8 bg-[#1D3557] text-white font-bold uppercase tracking-widest px-8 py-4 hover:bg-[#15293f] transition-colors text-sm disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="demo-create-btn"
          >
            {creating ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Building your preview...
              </>
            ) : (
              <>{sport.cta} <ArrowRight size={18} weight="bold" /></>
            )}
          </button>

          <p className="text-xs text-[#1D3557]/25 text-center mt-4">Free — no payment required. Your demo link is shareable.</p>
        </form>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-[#1D3557]/5 flex justify-center">
          <a href="https://www.targetfunder.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
            <img src={TF_LOGO} alt="TargetFunder" className="h-6 object-contain" />
            <span className="text-sm font-black uppercase tracking-tight text-[#1D3557]">
              target<span className="text-[#E63946]">funder</span><span className="text-[#1D3557]/30">.com</span>
            </span>
          </a>
        </div>
      </main>
    </div>
  );
}
